/* ==========================================================================
   Scroll-triggered reveal.

   One IntersectionObserver for the whole document, not one per element or per
   section. Elements opt in with `data-reveal` and are unobserved the moment
   they play, so the observer's list only ever shrinks.

   Vanilla, no dependency. Loaded deferred, so the DOM is parsed by the time
   this runs; the `.reveal-ready` class is set by a tiny inline snippet in the
   head instead, because it has to beat first paint.
   ========================================================================== */
(function () {
  'use strict';

  var SELECTOR = '[data-reveal]';

  /* The trigger is a share of the ELEMENT, not a line across the viewport.

     This started as rootMargin '0px 0px -15% 0px', which shrinks the observed
     area to the top 85% of the screen. That reads well while scrolling, but it
     strands anything whose resting position is inside the bottom 15%: the
     element is plainly on screen, never crosses the line, and stays at opacity 0
     for good. The product-tab heading did exactly that on a short mobile
     viewport - visible gap, no text.

     A ratio cannot do that. Anything fully on screen is at 1, so it always
     fires; a sliver peeking over the fold is near 0, so it still waits its turn.
     Same feel, no dead zone. */
  var REVEAL_RATIO = 0.15;

  /* 15% of an element taller than the screen can exceed the screen itself, which
     would be a dead zone of its own. Those reveal as soon as they appear. */
  var TALL_ELEMENT_FACTOR = 0.85;

  // Stagger: each step is 80ms and the total is capped so a long grid never
  // leaves the last card waiting. 400ms is the whole group's budget, not
  // per-item, so a 20-card list finishes as fast as a 5-card one.
  var STAGGER_STEP_MS = 80;
  var STAGGER_MAX_MS = 400;

  var root = document.documentElement;
  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var designMode = Boolean(window.Shopify && window.Shopify.designMode);

  /* Land an element in its final state. `instant` skips the transition, which
     is what above-the-fold content and the theme editor both want. */
  function show(el, instant) {
    if (instant) {
      el.classList.add('reveal--instant');
    }
    el.classList.remove('reveal--armed');
    el.classList.add('reveal--in');
    el.style.removeProperty('will-change');
  }

  /* No observer, no motion, or the merchant is editing: everything is visible
     immediately and nothing is ever armed. A merchant must never have to
     scroll to find content they are trying to edit. */
  function showAll(scope) {
    var nodes = (scope || document).querySelectorAll(SELECTOR);
    Array.prototype.forEach.call(nodes, function (el) {
      show(el, true);
    });
  }

  if (designMode || reduceMotion || !('IntersectionObserver' in window)) {
    // The hidden state is keyed off .reveal-ready, so simply never adding it
    // is enough - but the class is set inline in the head to beat first paint,
    // so drop it back off here and flush anything already marked.
    root.classList.remove('reveal-ready');
    showAll(document);

    if (designMode) {
      // Sections re-render as the merchant edits. Flush each new one too.
      ['shopify:section:load', 'shopify:section:reorder', 'shopify:block:select'].forEach(
        function (evt) {
          document.addEventListener(evt, function (event) {
            showAll(event.target && event.target.nodeType === 1 ? event.target : document);
          });
        }
      );
    }
    return;
  }

  /* True until the observer has reported once. Anything intersecting in that
     first batch was on screen before the user scrolled, so it is above the
     fold and must appear already-there rather than fading in late. */
  var firstPass = true;

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) {
        return;
      }

      /* Enough of the element on screen to be worth animating - or the element is
         taller than the screen can show 15% of, in which case any of it will do.
         rootBounds is null in some cross-origin cases, so the viewport height is
         the fallback measure. */
      var rootHeight = (entry.rootBounds && entry.rootBounds.height) || window.innerHeight || 0;
      var isTall = rootHeight > 0 && entry.boundingClientRect.height >= rootHeight * TALL_ELEMENT_FACTOR;

      if (entry.intersectionRatio < REVEAL_RATIO && !isTall) {
        return;
      }

      observer.unobserve(entry.target);
      show(entry.target, firstPass);
    });

    firstPass = false;
  }, { threshold: [0, REVEAL_RATIO] });

  /* Resolve the stagger for one element. An author-set --reveal-delay wins;
     otherwise data-reveal-index drives it. */
  function applyDelay(el) {
    if (el.style.getPropertyValue('--reveal-delay')) {
      return;
    }

    var raw = el.getAttribute('data-reveal-index');
    if (raw === null) {
      return;
    }

    var index = parseInt(raw, 10);
    if (isNaN(index) || index <= 0) {
      return;
    }

    var delay = Math.min(index * STAGGER_STEP_MS, STAGGER_MAX_MS);
    el.style.setProperty('--reveal-delay', delay + 'ms');
  }

  function scan(scope) {
    var nodes = (scope || document).querySelectorAll(SELECTOR);

    Array.prototype.forEach.call(nodes, function (el) {
      // Idempotent: re-scanning after a section reload must not re-arm an
      // element that has already played, or re-observe one already watched.
      if (el.classList.contains('reveal--in') || el.dataset.revealArmed === 'true') {
        return;
      }
      el.dataset.revealArmed = 'true';
      el.classList.add('reveal--armed');
      applyDelay(el);
      observer.observe(el);
    });
  }

  scan(document);

  /* The theme editor swaps whole sections out of the DOM. The observer holds
     no reference to the replacements, so without this the new markup keeps the
     hidden state forever. `firstPass` is deliberately re-armed: freshly loaded
     markup may well be on screen already, and should not fade in under the
     merchant's cursor. */
  ['shopify:section:load', 'shopify:section:reorder', 'shopify:block:select'].forEach(
    function (evt) {
      document.addEventListener(evt, function (event) {
        firstPass = true;
        scan(event.target && event.target.nodeType === 1 ? event.target : document);
      });
    }
  );

  // Exposed so a section that injects markup of its own can re-arm it.
  window.ForchicsReveal = { scan: scan, showAll: showAll };
})();
